import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { fetchCategories } from '../../services/api';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  
  const productsRef = useRef(null);
  const mediaRef = useRef(null);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await fetchCategories();
        setCategories(data.categories || []);
      } catch (error) {
        console.error('Error loading categories in Navbar:', error);
      }
    };
    loadCategories();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productsRef.current && !productsRef.current.contains(event.target)) {
        setIsProductsOpen(false);
      }
      if (mediaRef.current && !mediaRef.current.contains(event.target)) {
        setIsMediaOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLinkClick = () => {
    setIsOpen(false);
    setIsMediaOpen(false);
    setIsProductsOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      {/* Slogan Banner Bar */}
      <div className="w-full bg-gradient-to-r from-[#1E4C9A] to-[#1A8D32] text-white text-center py-2 px-4 text-xs sm:text-sm font-semibold tracking-wide shadow-inner">
        नवीनता र पर्यावरण, कृषि रुपान्तरण
      </div>
      <nav className="bg-white">
        <div className="w-full pl-0 pr-4 sm:pr-6 lg:pr-8">
          <div className="flex justify-between h-16 lg:h-20 items-center">
            {/* Left side - Logo and Company Name */}
            <div className="flex items-center min-w-0 h-full">
              <Link to="/" className="flex items-center min-w-0 h-full py-1.5" onClick={handleLinkClick}>
                <img
                  src="/assets/images/Banner.jpg"
                  alt="Innovative Eco Krishi Logo"
                  className="h-full w-auto shrink-0 object-contain transition-all duration-300 drop-shadow-sm"
                />
              </Link>
            </div>

            {/* Right side - Navigation */}
            <div className="hidden lg:flex lg:items-center lg:space-x-5 xl:space-x-8 ml-auto">
              <Link to="/" className="text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-[#1A8D32] font-medium" onClick={handleLinkClick}>
                Home
              </Link>
              <Link to="/about" className="text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-[#1A8D32] font-medium" onClick={handleLinkClick}>
                About
              </Link>

              {/* Products Dropdown */}
              <div
                className="relative"
                ref={productsRef}
                onMouseEnter={() => setIsProductsOpen(true)}
                onMouseLeave={() => setIsProductsOpen(false)}
              >
                <div className="flex items-center">
                  <Link
                    to="/products"
                    className="text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-[#1A8D32] font-medium group"
                    onClick={handleLinkClick}
                  >
                    <span>Products</span>
                    <svg className={`ml-1 h-4 w-4 transition-transform duration-200 ${isProductsOpen ? 'rotate-180 text-[#1A8D32]' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Link>
                </div>

                {/* Dropdown Menu */}
                {isProductsOpen && (
                  <div className="absolute left-0 mt-0 w-60 rounded-xl shadow-xl bg-white border border-gray-100 ring-1 ring-black ring-opacity-5 py-2 z-50">
                    <Link
                      to="/products"
                      className="block px-4 py-2.5 text-sm font-bold text-gray-900 hover:bg-green-50 hover:text-[#1A8D32] border-b border-gray-100 flex items-center justify-between"
                      onClick={handleLinkClick}
                    >
                      <span>All Products</span>
                      <span className="text-[11px] font-normal text-gray-400">View Catalog</span>
                    </Link>

                    <div className="pt-2">
                      <div className="px-4 py-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                        Categories
                      </div>
                      {categories.length === 0 ? (
                        <span className="block px-4 py-2 text-xs text-gray-400 italic">No categories found</span>
                      ) : (
                        categories.map((cat) => (
                          <Link
                            key={cat.category_id}
                            to={`/products?category=${encodeURIComponent(cat.name)}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-[#1A8D32] transition-colors flex items-center justify-between group"
                            onClick={handleLinkClick}
                          >
                            <span>{cat.name}</span>
                            <svg className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#1A8D32]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Media Dropdown */}
              <div
                className="relative"
                ref={mediaRef}
                onMouseEnter={() => setIsMediaOpen(true)}
                onMouseLeave={() => setIsMediaOpen(false)}
              >
                <button
                  onClick={() => setIsMediaOpen(!isMediaOpen)}
                  className="text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-[#1A8D32] font-medium"
                >
                  Media
                  <svg className={`ml-1 h-4 w-4 transition-transform duration-200 ${isMediaOpen ? 'rotate-180 text-[#1A8D32]' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {isMediaOpen && (
                  <div className="absolute right-0 mt-0 w-48 rounded-xl shadow-xl bg-white border border-gray-100 ring-1 ring-black ring-opacity-5 py-2 z-50">
                    <div className="py-1">
                      <Link to="/blog" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#1A8D32] hover:text-white transition-colors" onClick={handleLinkClick}>
                        Blogs
                      </Link>
                      <Link to="/gallery" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#1A8D32] hover:text-white transition-colors" onClick={handleLinkClick}>
                        Gallery
                      </Link>
                      <Link to="/notices" className="block px-4 py-2 text-sm text-gray-700 hover:bg-[#1A8D32] hover:text-white transition-colors" onClick={handleLinkClick}>
                        Notices
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <Link to="/investor-relations" className="text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-[#1A8D32] font-medium" onClick={handleLinkClick}>
                Reports
              </Link>
              <Link to="/contact" className="text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-[#1A8D32] font-medium" onClick={handleLinkClick}>
                Contact
              </Link>
              <Link to="/suggestion" className="text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 border-transparent hover:border-[#1A8D32] font-medium" onClick={handleLinkClick}>
                Innovative Suggestions
              </Link>
            </div>

            {/* Mobile/Tablet menu button */}
            <div className="flex items-center lg:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#1A8D32]"
              >
                <span className="sr-only">Open main menu</span>
                {!isOpen ? (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile/Tablet menu */}
        <div className={`${isOpen ? 'block' : 'hidden'} lg:hidden border-t border-gray-100`}>
          <div className="pt-2 pb-3 space-y-1">
            <Link to="/" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-[#1A8D32]" onClick={handleLinkClick}>
              Home
            </Link>
            <Link to="/about" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-[#1A8D32]" onClick={handleLinkClick}>
              About
            </Link>

            {/* Mobile Products Menu */}
            <div>
              <div className="flex items-center justify-between pl-3 pr-4 py-2 border-l-4 border-transparent hover:bg-gray-50 hover:border-[#1A8D32]">
                <Link to="/products" className="text-base font-medium text-gray-600 hover:text-gray-800 flex-1" onClick={handleLinkClick}>
                  Products
                </Link>
                <button
                  onClick={() => setIsProductsOpen(!isProductsOpen)}
                  className="p-1 text-gray-500 hover:text-[#1A8D32] focus:outline-none"
                >
                  <svg className={`w-5 h-5 transition-transform duration-200 ${isProductsOpen ? 'rotate-180 text-[#1A8D32]' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {isProductsOpen && (
                <div className="pl-6 pr-4 py-1 space-y-1 bg-gray-50 border-l-2 border-[#1A8D32] my-1">
                  <Link to="/products" className="block py-1.5 text-sm font-semibold text-gray-800 hover:text-[#1A8D32]" onClick={handleLinkClick}>
                    All Products
                  </Link>
                  {categories.map((cat) => (
                    <Link
                      key={cat.category_id}
                      to={`/products?category=${encodeURIComponent(cat.name)}`}
                      className="block py-1.5 text-sm text-gray-600 hover:text-[#1A8D32]"
                      onClick={handleLinkClick}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Media Menu */}
            <div className="pl-3 pr-4 py-2">
              <button
                onClick={() => setIsMediaOpen(!isMediaOpen)}
                className="w-full text-left text-base font-medium text-gray-600 hover:text-gray-800 flex items-center justify-between"
              >
                <span>Media</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${isMediaOpen ? 'rotate-180 text-[#1A8D32]' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {isMediaOpen && (
                <div className="pl-4 mt-2 space-y-1 border-l-2 border-[#1A8D32]">
                  <Link to="/blog" className="block py-2 text-base font-medium text-gray-600 hover:text-[#1A8D32]" onClick={handleLinkClick}>
                    Blogs
                  </Link>
                  <Link to="/gallery" className="block py-2 text-base font-medium text-gray-600 hover:text-[#1A8D32]" onClick={handleLinkClick}>
                    Gallery
                  </Link>
                  <Link to="/notices" className="block py-2 text-base font-medium text-gray-600 hover:text-[#1A8D32]" onClick={handleLinkClick}>
                    Notices
                  </Link>
                </div>
              )}
            </div>

            <Link to="/investor-relations" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-[#1A8D32]" onClick={handleLinkClick}>
              Reports
            </Link>
            <Link to="/contact" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-[#1A8D32]" onClick={handleLinkClick}>
              Contact
            </Link>
            <Link to="/suggestion" className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-50 hover:border-[#1A8D32]" onClick={handleLinkClick}>
              Suggestion
            </Link>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Navbar;